import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PenLine, Upload, Trash2, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Checkerboard so transparency in the PNG is visible in the preview.
const CHECKER =
  "repeating-conic-gradient(#e5e5e5 0% 25%, #ffffff 0% 50%) 50% / 14px 14px";

/**
 * Reusable transparent-PNG signature uploader.
 * Props:
 *  - signatureUrl: string|null (relative /api/... path)
 *  - onUpload: async (dataUri) => void   (persist to backend)
 *  - onRemove: async () => void          (clear on backend)
 *  - disabled: bool
 *  - testid: string prefix
 */
export default function SignatureUpload({ signatureUrl, onUpload, onRemove, disabled, testid = "signature" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.includes("png")) {
      toast.error("La firma debe ser un PNG con fondo transparente");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen es muy grande (máx. 3 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await onUpload(reader.result);
        toast.success("Firma guardada");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Error al guardar la firma");
      } finally {
        setBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await onRemove();
      toast.success("Firma eliminada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar la firma");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`${testid}-upload`}>
      <Label className="text-xs font-semibold mb-1 flex items-center gap-1.5">
        <PenLine className="w-3.5 h-3.5" /> Firma (PNG transparente)
      </Label>
      <p className="text-[11px] text-muted-foreground mb-2">
        Se usa en los reportes fotográficos MIP como firma de quien realiza el reporte.
      </p>
      {signatureUrl ? (
        <div className="flex items-center gap-3">
          <div
            className="h-16 w-40 rounded-lg border flex items-center justify-center overflow-hidden"
            style={{ background: CHECKER }}
          >
            <img
              src={`${API}${signatureUrl}`}
              alt="Firma"
              className="max-h-14 max-w-[150px] object-contain"
              data-testid={`${testid}-preview`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Button type="button" variant="outline" size="sm" disabled={busy || disabled}
              onClick={() => inputRef.current?.click()} data-testid={`${testid}-replace-btn`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
              Reemplazar
            </Button>
            <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700"
              disabled={busy || disabled} onClick={handleRemove} data-testid={`${testid}-remove-btn`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" disabled={busy || disabled}
          onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload-btn`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          Subir firma PNG
        </Button>
      )}
      <input ref={inputRef} type="file" accept="image/png" className="hidden" onChange={handleFile}
        data-testid={`${testid}-input`} />
    </div>
  );
}
